/**
 * The vendor-neutral analytics contract: event names, the funnel, the
 * dimensions, and the property scrubbing every destination shares.
 *
 * Split out of `ga4.ts` because that file had outgrown its name. GA4 is one
 * destination out of six planned (GA4, PostHog, Meta, TikTok, LinkedIn, Google
 * Ads), and the names, aliases and scrubbing are common to all of them. Leaving
 * them in a file called `ga4` invites the next person adding a Meta tag to
 * write a second copy of the same rules, and the two then drift.
 *
 * The division of labour is the important part:
 *
 *   this file          what an event IS, and what is safe to send anywhere
 *   ga4.ts             GA4's own limits, allow-list and Consent Mode
 *   <vendor>.ts        that vendor's limits and standard-event mapping
 *
 * Lossy transforms belong in the vendor adapter, never here. A cap applied at
 * this layer is inherited by every destination, which is exactly the mistake
 * this split exists to prevent: GA4 keeps 25 parameters per event, and trimming
 * to 25 before the fan-out would hand Meta and TikTok GA4's leftovers even
 * where they had room for more.
 */

/**
 * The funnel, in order. Names are deliberately literal rather than GA4's
 * "recommended events" (`sign_up`, `begin_checkout`, ...) so that a funnel
 * exploration reads the same as the spec the team agreed on.
 *
 * `purchase` is the one exception: GA4's monetization reports, revenue metrics
 * and the `value`/`currency`/`transaction_id` schema are hard-wired to that
 * exact name, and Meta, TikTok and Google Ads all use it as their standard
 * purchase event too, so renaming it would silently zero out revenue reporting
 * in four places at once.
 */
export const ANALYTICS_EVENTS = {
  landingView: "landing_view",
  ctaClick: "cta_click",
  signupStarted: "signup_started",
  signupCompleted: "signup_completed",
  onboardingCompleted: "onboarding_completed",
  checkoutStarted: "checkout_started",
  trialStarted: "trial_started",
  subscriptionStarted: "subscription_started",
  purchase: "purchase",
  /** Server-side, from the RevenueCat CANCELLATION webhook. Not a funnel step. */
  subscriptionCancelled: "subscription_cancelled",
  /** Server-side, RevenueCat EXPIRATION: access actually ended. The churn date. */
  subscriptionExpired: "subscription_expired",
  /** Server-side, RevenueCat BILLING_ISSUE: a renewal charge failed and the grace period is running. */
  subscriptionBillingIssue: "subscription_billing_issue_detected",
  /** Server-side, RevenueCat PRODUCT_CHANGE: upgrade, downgrade or billing-interval change. */
  subscriptionPlanChanged: "subscription_plan_changed",
  /** Server-side, RevenueCat UNCANCELLATION: auto-renew switched back on before expiry. */
  subscriptionReactivated: "subscription_reactivated",
  /**
   * Server-side. GA4's standard ecommerce refund event, so reported revenue is
   * net of refunds; the name is fixed by GA4 exactly like `purchase`.
   */
  refund: "refund",
  /** Server-side: an owner tried to seat a member and the pool was full. */
  seatCapacityBlocked: "subscription_seat_capacity_blocked",
  /** Server-side and pseudonymous: an account was deleted, with the stated reason category. */
  accountDeleted: "account_deleted",
  /** Server-side from the public profile beacon, visitor consent explicit: a page of a SmartCV or company hub was viewed. */
  publicProfileViewed: "public_profile_viewed",
  /** Server-side from the profile redirect: a visitor clicked a contact channel on a public profile. */
  publicProfileContactClicked: "public_profile_contact_clicked",
  /** Server-side from the profile redirect: a visitor downloaded a CV from a public page or share link. */
  cvDownloadedByVisitor: "cv_downloaded_by_visitor",
  /** A visitor clicked apply on a job: the jobs tree on the web, or a company hub role through the redirect. */
  jobApplyClicked: "job_apply_clicked",
  /** Server-side: an AI action was refused because a daily, cycle or workspace limit was reached. */
  aiActionLimitReached: "ai_action_limit_reached",
  /** Server-side: the auto-apply worker submitted an application on the user's behalf. */
  autoApplySubmitted: "auto_apply_submitted",
  /** Server-side: a public apply flow turned an applicant into an account. */
  publicApplyProfileClaimed: "public_apply_profile_claimed",
  /** Server-side: a signup was attributed to a partner referral code. */
  partnerReferralAttributed: "partner_referral_attributed",
  /** The homepage's primary action: a resume was handed to the app for import. */
  resumeHandoffStarted: "resume_handoff_started",
  /** A public skill page's composer sent a CV in to apply that skill. */
  skillApplyStarted: "skill_apply_started",
  /**
   * The public CV Health funnel (cvhealth.tabbio.com), web-side because the
   * visitor is anonymous. Properties are bounded bands and enums only: never
   * CV text, file names, emails or report tokens. The fix CTA itself is
   * `cta_click` with `cta_id: cv_health_fix`.
   */
  /** A CV (file or pasted text) was sent for a free check (`input_kind`, `source`). */
  cvHealthPublicReviewStarted: "cv_health_public_review_started",
  /** A check this page started settled (`status`, `score_band`, `count_band`, `ai_status`). */
  cvHealthPublicReviewCompleted: "cv_health_public_review_completed",
  /** A check could not start or failed (`error_code`). */
  cvHealthPublicReviewFailed: "cv_health_public_review_failed",
  /** A report was shown, once per report and tab (`via`, `status`, bands). */
  cvHealthPublicReportViewed: "cv_health_public_report_viewed",
  /** An audience row was opened (`area`: human, system, ai_bot). */
  cvHealthPublicAreaOpened: "cv_health_public_area_opened",
  /** The lock on a teased suggestion was followed into the app. */
  cvHealthPublicTeaserLockedClicked: "cv_health_public_teaser_locked_clicked",
  /** The visitor asked for the report by email: a lead (`delivered`). */
  cvHealthPublicEmailCaptured: "cv_health_public_email_captured",
  /** The report link was copied or shared (`method`: copy, native). */
  cvHealthPublicShareUsed: "cv_health_public_share_used",
  /** A marketing tool emailed its report: the only email capture on the site. */
  toolLeadCaptured: "tool_lead_captured",
  /** A visitor sent the contact form on a public profile. */
  profileContactFormSubmitted: "profile_contact_form_submitted",
  /** The claim sheet on a public profile was shown: the denominator for profile → signup. */
  profileUpsellShown: "profile_upsell_shown",
  /** One step of the public apply flow was completed (`step`, `step_index`, `total_steps`). */
  applyStepCompleted: "apply_step_completed",
  /** The consent banner was shown; the denominator for the consent rate. */
  consentBannerShown: "consent_banner_shown",
  /** A consent decision was saved; declines reach GA4 only as cookieless pings and never reach PostHog. */
  consentDecisionSaved: "consent_decision_saved",
  consentPreferencesOpened: "consent_preferences_opened",
  /** A 404 boundary rendered; broken backlinks and retired URLs stop looking like traffic. */
  pageNotFound: "page_not_found",
  /** Server-side: an application landed on an employer's job, attributed to the job's owner. */
  applicationReceived: "application_received",
  /** A visitor submitted the marketing site's claim-your-handle form. A pixel lead. */
  handleClaimStarted: "handle_claim_started",
  /** A visitor submitted the employer early-access form. A pixel lead. */
  employerLeadSubmitted: "employer_lead_submitted",
} as const;

export type AnalyticsEventName =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

/**
 * The canonical funnel, in step order, for GA4's "Funnel exploration" report
 * and PostHog's funnel insight.
 *
 * `landing_view` is step 1 rather than `page_view` so the funnel measures
 * marketing landings, not every in-app navigation.
 */
export const FUNNEL_STEPS: readonly AnalyticsEventName[] = [
  ANALYTICS_EVENTS.landingView,
  ANALYTICS_EVENTS.ctaClick,
  ANALYTICS_EVENTS.signupStarted,
  ANALYTICS_EVENTS.signupCompleted,
  ANALYTICS_EVENTS.onboardingCompleted,
  ANALYTICS_EVENTS.subscriptionStarted,
];

/**
 * Events that count as a conversion. In GA4 these are marked as key events, in
 * PostHog they are the funnel's terminal steps, and in the ad platforms they
 * are the conversion actions bidding optimizes towards. Marking them is a UI
 * action with no code equivalent, so this list exists to make the checklist
 * reviewable.
 *
 * `landing_view` is intentionally absent: a landing is the funnel's entry, not
 * a conversion, and counting it reports the conversion rate as ~100%.
 */
export const KEY_EVENTS: readonly AnalyticsEventName[] = [
  ANALYTICS_EVENTS.signupCompleted,
  ANALYTICS_EVENTS.onboardingCompleted,
  ANALYTICS_EVENTS.employerLeadSubmitted,
  ANALYTICS_EVENTS.toolLeadCaptured,
  ANALYTICS_EVENTS.trialStarted,
  ANALYTICS_EVENTS.purchase,
];

/**
 * Product-specific dimensions, to register wherever a destination requires
 * declaring them up front (GA4 custom definitions, PostHog property display).
 *
 * A GA4 custom parameter that is not registered is still collected and still
 * visible in Realtime, but cannot be used as a dimension in any standard report
 * or exploration, which is the usual reason a team believes an event "isn't
 * tracking". Registration does not backfill historical dimension data, so register
 * before relying on these fields in reports.
 */
export const CUSTOM_DIMENSIONS = [
  {
    parameter: "analytics_granted",
    displayName: "Analytics consent",
    description: "The analytics purpose choice on a consent decision.",
  },
  {
    parameter: "marketing_granted",
    displayName: "Marketing consent",
    description: "The advertising purpose choice on a consent decision.",
  },
  {
    parameter: "decision_source",
    displayName: "Consent decision source",
    description: "Banner or preferences dialog.",
  },
  {
    parameter: "measurement_basis",
    displayName: "Revenue attribution basis",
    description:
      "checkout_session, first_acquisition or unattributed; never an old signup session on a renewal.",
  },

  {
    parameter: "surface",
    displayName: "Surface",
    description: "Which client sent the event: web, app_web, ios or android.",
  },
  {
    parameter: "locale",
    displayName: "Locale",
    description: "UI language at the time of the event (en or ar).",
  },
  {
    parameter: "cta_id",
    displayName: "CTA ID",
    description:
      "Stable identifier of the clicked call to action, e.g. hero_primary.",
  },
  {
    parameter: "cta_location",
    displayName: "CTA location",
    description:
      "Page region the CTA was clicked in, e.g. hero, pricing, footer.",
  },
  {
    parameter: "cta_destination",
    displayName: "CTA destination",
    description: "Path or host the CTA sent the visitor to.",
  },
  {
    parameter: "signup_method",
    displayName: "Signup method",
    description: "email, google, apple or uaepass.",
  },
  {
    parameter: "plan_tier",
    displayName: "Plan tier",
    description: "Subscription tier involved in a checkout or purchase.",
  },
  {
    parameter: "billing_interval",
    displayName: "Billing interval",
    description: "monthly or annual.",
  },
  {
    parameter: "user_role",
    displayName: "User role",
    description: "candidate, employer or admin.",
  },
  {
    parameter: "page_type",
    displayName: "Page type",
    description:
      "Marketing template behind the landing, e.g. home, tool, blog, job.",
  },
  {
    parameter: "feature",
    displayName: "Feature",
    description:
      "Service behind a feature_used event: cover_letter, interview_prep, salary_insights, application_pack, recruiter_preview, library, shortlists.",
  },
  {
    parameter: "action",
    displayName: "Feature action",
    description:
      "What was done with the feature: started, created, completed, viewed.",
  },
  {
    parameter: "tool",
    displayName: "Tool",
    description:
      "Marketing-site tool behind a tool_used event: salary_calculator, cv_score, cover_letter, jd_generator, gratuity_calculator.",
  },
  {
    parameter: "channel",
    displayName: "Share channel",
    description:
      "How a CV was shared: copy_link, native_share, qr, qr_download, whatsapp, linkedin, telegram, email.",
  },
  {
    parameter: "plan_set",
    displayName: "Plan set",
    description:
      "individual or employer, on pricing, checkout and revenue events.",
  },
  {
    parameter: "mode",
    displayName: "Mode",
    description:
      "Variant of an action: native or external for job applications, the agent mode for chat messages.",
  },
  {
    parameter: "source",
    displayName: "Source",
    description:
      "Where an action originated: onboarding, settings, manual, catalog, ai, tabbio_app.",
  },
  {
    parameter: "reason",
    displayName: "Reason",
    description:
      "Why it happened: RevenueCat cancel or expiration reason, account deletion reason category.",
  },
  {
    parameter: "previous_plan_tier",
    displayName: "Previous plan tier",
    description: "Tier before a plan change; plan_tier carries the new one.",
  },
  {
    parameter: "step",
    displayName: "Step",
    description:
      "The funnel step a multi-step flow event refers to, e.g. upload or verify.",
  },
] as const satisfies readonly {
  parameter: string;
  displayName: string;
  description: string;
}[];

export type CustomDimension = (typeof CUSTOM_DIMENSIONS)[number];

/**
 * Internal `trackProductEvent` names that already exist in `apps/mobile` and
 * mean the same thing as a funnel step.
 *
 * The app has ~200 product-analytics call sites feeding PostHog and Sentry.
 * Re-instrumenting the funnel steps at each of those would mean two
 * near-identical calls per site and two things to keep in sync; aliasing them in
 * one table instead means the funnel is fed by the instrumentation the app
 * already has, and a renamed internal event fails a test here rather than
 * silently dropping a funnel step.
 *
 * Events absent from this table keep their own name.
 */
export const PRODUCT_EVENT_ALIASES: Readonly<Record<string, string>> = {
  onboarding_completed_client: ANALYTICS_EVENTS.onboardingCompleted,
  onboarding_session_started: "onboarding_started",
  onboarding_hiring_company_created: "company_created",
  nova_automation_activated: "automation_activated",
  subscription_checkout_started: ANALYTICS_EVENTS.checkoutStarted,
  subscription_purchase_started: ANALYTICS_EVENTS.checkoutStarted,
  subscription_purchase_completed: ANALYTICS_EVENTS.subscriptionStarted,
  paywall_purchase_completed: ANALYTICS_EVENTS.subscriptionStarted,
};

/** Resolves an internal product event name to its canonical funnel name. */
export function toCanonicalEventName(event: string): string {
  return PRODUCT_EVENT_ALIASES[event] ?? event;
}

/** Whether a name is one of the canonical funnel events. */
export function isFunnelEvent(event: string): event is AnalyticsEventName {
  return (Object.values(ANALYTICS_EVENTS) as string[]).includes(event);
}

/**
 * Events that must never wait for an idle flush.
 *
 * Two kinds. Conversions (`KEY_EVENTS`): losing one is losing the number the
 * whole stack exists to produce. And `cta_click`: it fires immediately before a
 * cross-document navigation, and a deferred callback is not guaranteed to run
 * before the document goes away. `checkout_started` is both, since it precedes
 * the redirect to the payment host.
 *
 * Takes the canonical name; callers alias internal names first.
 */
export function isCriticalAnalyticsEvent(event: string): boolean {
  return (
    event === ANALYTICS_EVENTS.ctaClick ||
    event === ANALYTICS_EVENTS.signupStarted ||
    event === ANALYTICS_EVENTS.checkoutStarted ||
    event === ANALYTICS_EVENTS.subscriptionStarted ||
    (KEY_EVENTS as readonly string[]).includes(event)
  );
}

/**
 * Decision D7 (2026-09-11): signups and product milestones are reported
 * **separately**, per role, and are never merged into one "activated" flag or
 * a time window. A signup is an acquisition number; a CV event is a usage
 * number. The dashboard shows them side by side, funnel F5 lists the stages
 * without gating one on the other, and workflow W2 keys off the absence of a
 * usage event, not off a window after signup. Employers the same way: signup,
 * then `job_posted`.
 *
 * Every name here is a canonical, GA4 allow-listed event (pinned by the test).
 */
export const ROLE_MILESTONE_EVENTS = {
  candidate: {
    signup: "signup_completed",
    usage: [
      "cv_created",
      "cv_imported",
      "cv_exported",
      "cv_shared",
      "job_apply_completed",
    ],
  },
  employer: {
    signup: "signup_completed",
    usage: ["job_posted"],
  },
} as const satisfies Readonly<
  Record<"candidate" | "employer", { signup: string; usage: readonly string[] }>
>;

export type MilestoneRole = keyof typeof ROLE_MILESTONE_EVENTS;

/**
 * Whether `event` (any alias; canonicalised here) is one of `role`'s usage
 * milestones. Signups deliberately never match: they are counted on their own.
 */
export function isUsageMilestoneEvent(
  event: string,
  role: MilestoneRole,
): boolean {
  return (ROLE_MILESTONE_EVENTS[role].usage as readonly string[]).includes(
    toCanonicalEventName(event),
  );
}

export type EventPropertyValue = string | number | boolean;

/**
 * A ceiling on how many properties survive scrubbing. `0` means none.
 *
 * Decided 2026-09-09: no cap here. This is not a per-vendor limit: every
 * destination's real cap (GA4's 25 in `applyGa4Limits`, and whatever Meta and
 * TikTok enforce) belongs in that destination's adapter, and PostHog, the
 * long-tail store, has no limit worth enforcing. A runaway call site would
 * show up as a PostHog property explosion, which is visible and cheap, rather
 * than as silently truncated GA4 data, which is neither.
 */
export const MAX_EVENT_PROPERTIES = 0;

/**
 * Coerces arbitrary event properties into a shape any destination can store.
 *
 * Vendor-neutral on purpose. It does the work that is right everywhere:
 *
 *   - drops `null`, `undefined` and `""`, which GA4 stores as the literal
 *     strings "null" and "undefined" and which then pollute every report the
 *     dimension appears in;
 *   - drops `NaN` and `Infinity`, which serialize to `null` in JSON and arrive
 *     as a hole in a numeric report;
 *   - snake_cases keys and strips PostHog's `$` prefixes, since our call sites
 *     mix camelCase with PostHog conventions and no destination accepts both;
 *   - flattens objects to JSON rather than shipping "[object Object]".
 *
 * It deliberately does NOT truncate values or apply reserved-prefix rules:
 * those are per-vendor and belong in the adapters. Keys are sorted so that the
 * result is deterministic rather than dependent on object insertion order.
 *
 * Note this runs *after* `sanitizeObservabilityProperties` in `apps/mobile`,
 * which is the layer that removes personal data (email, name, phone, URLs, CV
 * text). This function is about shape, not privacy.
 */
export function sanitizeEventProperties(
  properties?: Record<string, unknown> | null,
): Record<string, EventPropertyValue> {
  if (!properties) return {};

  const result: Record<string, EventPropertyValue> = {};
  for (const key of Object.keys(properties).sort()) {
    if (
      MAX_EVENT_PROPERTIES > 0 &&
      Object.keys(result).length >= MAX_EVENT_PROPERTIES
    ) {
      break;
    }

    const value = properties[key];
    if (value === null || value === undefined || value === "") continue;

    const name = toEventPropertyName(key);
    if (!name) continue;

    if (typeof value === "number") {
      if (!Number.isFinite(value)) continue;
      result[name] = value;
      continue;
    }
    if (typeof value === "boolean") {
      result[name] = value;
      continue;
    }

    const text = typeof value === "string" ? value : safeStringify(value);
    if (!text) continue;
    result[name] = text;
  }

  return result;
}

/**
 * Normalizes a property key to snake_case.
 *
 * PostHog's `$`-prefixed conventions (`$identify`, `$anon_distinct_id`) and our
 * camelCase props are both in play at the call sites, and no destination accepts
 * both spellings as the same dimension.
 */
function toEventPropertyName(key: string): string | null {
  const snake = key
    .replace(/^\$+/, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^A-Za-z0-9_]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();

  if (!snake || !/^[a-z]/.test(snake)) return null;
  return snake;
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value) ?? "";
  } catch {
    return "";
  }
}
